import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {first} from 'rxjs/operators';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subscription} from 'rxjs';

export interface Feature {
    color: string;
    bold?: boolean;
}

export interface Features {
    keywords?: { [keyword: string]: Feature };
}

@Component({
    selector: 'app-expression-editor',
    templateUrl: './expression-editor.component.html',
    styleUrls: ['./expression-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ExpressionEditorComponent),
            multi: true
        }
    ]
})
export class ExpressionEditorComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnChanges {

    private changeSubscription: Subscription;
    @ViewChild('content') private contentElement: ElementRef;
    private chunker: (text: string) => string;

    @Input('algorithmMode') algorithmMode: boolean;
    @Input('content') content: string;
    @Input('features') features: Features;
    @Input('disabled') disabled: boolean;
    @Output('contentChange') contentEmitter: EventEmitter<string> = new EventEmitter<string>();

    contentHtml: SafeHtml;

    constructor(private ngZone: NgZone,
                private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly sanitizer: DomSanitizer) {
        this.changeSubscription = null;
        this.chunker = text => text;
        this.contentHtml = null;
    }

    registerOnChange(fn: any): void {
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
        }
        this.changeSubscription = this.contentEmitter.subscribe(content => {
            fn(content);
        });
    }

    registerOnTouched(fn: any): void {
        if (this.contentElement) { // to avoid initial call before component creation has been completed
            (this.contentElement.nativeElement as HTMLElement).focus();
        }
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(obj: any): void {
        this.content = obj;
        if (this.contentElement) { // to avoid initial value setting when component has not been initialized yet
            this.updateContent(this.content);
            this.changeDetectorRef.markForCheck();
        }
    }

    ngOnInit() {
    }

    ngAfterViewInit() {
        this.updateChunker(this.features);
        this.updateContent(this.content);
        setTimeout(() => this.changeDetectorRef.markForCheck(), 0);
    }

    ngOnChanges(changes: SimpleChanges) {
        const contentChanged = 'content' in changes && !changes.content.isFirstChange() && changes.content.currentValue !== changes.content.previousValue;
        const featuresChanged = 'features' in changes && !changes.features.isFirstChange();
        if (featuresChanged) {
            this.updateChunker(this.features);
        }
        if (contentChanged || featuresChanged) {
            this.updateContent(changes.content.currentValue);
            this.changeDetectorRef.markForCheck();
        }
    }

    ngOnDestroy() {
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
        }
    }

    onClickContainer(event: MouseEvent) {
        (this.contentElement.nativeElement as HTMLElement).focus();
    }

    onContentInput(event: Event) {
        const text = this.getContentText();
        this.updateContent(text);
        this.changeDetectorRef.markForCheck();
        this.contentEmitter.emit(text);
        this.content = text;
    }

    private updateContent(content: string) {
        let markup: string;
        if (!content || !content.length) {
            // leave a dummy node just to keep the cursor visible
            markup = '<span></span>';
        } else {
            markup = this.chunker(content);
        }

        // update content's inner HTML
        this.contentHtml = this.sanitizer.bypassSecurityTrustHtml(markup);

        // determine currently selected text range
        let selectedChRange = this.getSelectedChRange();

        // adjust with respect to previous selection (*after* new HTML is rendered)
        if (selectedChRange) {
            this.ngZone.onStable.pipe(
                first()
            ).subscribe(() => {
                const [startCh, endCh] = selectedChRange;
                // NOTE: startCh may be greater than endCh
                let [startNode, startOffset] = findNodeFromChOffset(this.contentElement.nativeElement, startCh);
                let [endNode, endOffset] = findNodeFromChOffset(this.contentElement.nativeElement, endCh);
                if (startNode && endNode) { // consistency check (e.g. content might me empty, therefore no spans)
                    const selection = window.getSelection();
                    const range = document.createRange();
                    range.setStart(startNode, startOffset);
                    range.setEnd(endNode, endOffset);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            });
        }
    }

    private updateChunker(features: Features) {
        const keywordColors = this.features ? (this.features.keywords || {}) : {};
        const keywords = Object.keys(keywordColors);
        const keywordRegExp = keywords.length ? new RegExp(`(?<=\\W|^)(${keywords.join('|')})(?=\\W|$)`) : null;
        this.chunker = (text: string) => keywordRegExp ?
            text.split(keywordRegExp)
                .filter(Boolean)
                .reduce((acc, chunk) => acc + (chunk in keywordColors ?
                    mkChunkNode(chunk, keywordColors[chunk]) :
                    `<span>${chunk}</span>`),
                    '') :
            `<span>${text}</span>`;
    }

    private getContentText(): string {
        let root = this.contentElement.nativeElement as Node;
        let text = '';

        function parseNode(n: Node) {
            if (n.hasChildNodes()) {
                n.childNodes.forEach(parseNode);
            } else {
                // leaves can only be text nodes or BRs
                if (n.nodeType === Node.TEXT_NODE) {
                    text += n.textContent;
                } else if (n.nodeName === 'BR') {
                    text += '\n';
                }
            }
        }

        root.childNodes.forEach(parseNode);
        return text;
    }

    private getSelectedChRange(): [number, number] {
        // check that we have a selection
        const selection = window.getSelection();
        if (selection && selection.rangeCount) {
            // check that we are inside the component area
            const content = this.contentElement.nativeElement as HTMLElement;
            const range = selection.getRangeAt(0);
            if (rangeInElement(range, content)) {
                // check for a consistent selection
                if (range.startContainer && range.endContainer) {
                    return [
                        findChOffsetFromNode(range.startContainer, range.startOffset, content),
                        findChOffsetFromNode(range.endContainer, range.endOffset, content)
                    ];
                }
            }
        }
        return null;
    }
}

function findChOffsetFromNode(startNode: Node, offset: number, root: Node): number {
    let chOffset = 0;
    let node: Node = startNode;
    if (startNode.nodeType === Node.ELEMENT_NODE) {
        if (startNode.hasChildNodes()) {
            node = startNode.childNodes[offset];
        }
        chOffset = 0;
    } else if (startNode.nodeType === Node.TEXT_NODE) {
        chOffset = offset;
    }
    if (node === root) {
        return chOffset; // already set to 0
    }
    do {
        while (true) {
            if (node.previousSibling) {
                node = node.previousSibling;
                if (node.nodeName === 'BR') {
                    chOffset++;
                } else {
                    chOffset += node.textContent.length;
                }
            } else {
                break;
            }
        }
        node = node.parentNode;
    } while (node !== root);
    return chOffset;
}

function findNodeFromChOffset(root: Node, chOffset: number): [Node, number] {
    if (!root.hasChildNodes()) {
        return [null, 0];
    }
    let offset = 0;

    function parseNode(node: Node): [Node, number] {
        if (node.hasChildNodes()) {
            let child = node.childNodes[0];
            while (child) {
                const r = parseNode(child);
                if (r[0]) {
                    return r;
                }
                child = child.nextSibling;
            }
        } else {
            // leaves can only be text nodes or BRs
            let nodeLen = 0;
            if (node.nodeType === Node.TEXT_NODE) {
                nodeLen = node.textContent.length;
            } else if (node.nodeName === 'BR') {
                nodeLen = 1;
            }
            if (offset + nodeLen > chOffset) {
                return [node, chOffset - offset];
            }
            offset += nodeLen;
        }
        return [null, 0];
    }

    const r = parseNode(root);
    if (r[0]) {
        return r;
    }
    // workaround for selecting "after" last span
    if (offset === chOffset) {
        let lastChild = root;
        while (lastChild.hasChildNodes()) {
            lastChild = lastChild.lastChild;
        }
        if (lastChild.nodeType === Node.TEXT_NODE) {
            return [lastChild, lastChild.textContent.length];
        } else if (lastChild.nodeName === 'BR') {
            return [lastChild, 1];
        }
    }
    return [null, 0];
}

function rangeInElement(range: Range, element: HTMLElement): boolean {
    // return range.startContainer && isChildOf(range.startContainer, element) &&
    //     range.endContainer && isChildOf(range.endContainer, element);
    const elRange = document.createRange();
    elRange.selectNodeContents(element);
    return rangeContains(elRange, range);
}

function rangeContains(outer: Range, inner: Range): boolean {
    return (
        outer.compareBoundaryPoints(Range.START_TO_START, inner) <= 0 &&
        outer.compareBoundaryPoints(Range.END_TO_END, inner) >= 0
    );
}

function mkChunkNode(chunk: string, feature: Feature): string {
    const node = feature.bold ? `<strong>${chunk}</strong>` : chunk;
    return `<span style="color:${feature.color}">${node}</span>`;
}
